import React, { useEffect, useRef, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { HubShell } from "../components/hub/shell";
import { SkeletonCard } from "../components/shared/Skeleton";
import TranscriptEvaluationExportModal from "../components/TranscriptEvaluationExportModal";
import TranscriptSyncModal from "../components/TranscriptSyncModal";
import ClientFeatureToggles from "../components/ClientFeatureToggles";
import { useNotification } from "../contexts/NotificationContext";
import { useView } from "../contexts/ViewContext";
import {
  getAllProjects,
  getAllCompanies,
  createProject,
  updateProject,
  deleteProject,
  uploadProjectLogo,
  addProjectToCompany,
  removeProjectFromCompany,
} from "../api";

const ProjectManagementPage = () => {
  const navigate = useNavigate();
  const { showSuccess, showError, showConfirm } = useNotification();
  // Multi-Org Membership Epic 1 — role follows the active org (not localStorage).
  const { refreshProjects: refreshViewProjects, refreshClientFlags, role: currentUserRole } = useView();
  const username = localStorage.getItem("username") || "admin";

  const [projects, setProjects] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  const featureTogglesRef = useRef(null);

  // Filters
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [aiFilter, setAiFilter] = useState("all");
  const [sortBy, setSortBy] = useState("name");

  // Modal states
  const [showAddEditModal, setShowAddEditModal] = useState(false);
  const [showAssignCompanyModal, setShowAssignCompanyModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Form data — only fields that map to real Supabase columns.
  // Per-client dashboard toggles live in `feature_flags` (scope='client');
  // ClientFeatureToggles owns that state directly. Voiceflow credentials are
  // managed elsewhere via VoiceflowIntegrationSection.
  const [formData, setFormData] = useState({
    name: "",
    description: "",
    type: "chat",
    group_name: "",
    status: "1",
    ai_enabled: false,
    ai_chatbot_enabled: false,
    ai_phone_agent_enabled: false,
    company_id: "",
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  useEffect(() => {
    // Check role - only allow Super Admin (0) and Admin (1)
    if (![0, 1].includes(currentUserRole)) {
      navigate("/dashboard");
      return;
    }
    fetchData();
  }, [currentUserRole, navigate]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [projectsRes, companiesRes] = await Promise.all([
        getAllProjects(true), // Get all projects (admin mode)
        getAllCompanies(),
      ]);
      setProjects(projectsRes?.data || projectsRes || []);
      setCompanies(companiesRes?.data || companiesRes || []);
    } catch (err) {
      showError("Error", err?.message || "Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  // Filter and sort projects
  const filteredProjects = useMemo(() => {
    let result = [...projects];

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name?.toLowerCase().includes(query) ||
          p.description?.toLowerCase().includes(query) ||
          p.group_name?.toLowerCase().includes(query)
      );
    }

    // Type filter
    if (typeFilter !== "all") {
      result = result.filter((p) => p.type === typeFilter);
    }

    // Status filter
    if (statusFilter !== "all") {
      result = result.filter((p) => String(p.status) === statusFilter);
    }

    // Company filter
    if (companyFilter !== "all") {
      if (companyFilter === "unassigned") {
        result = result.filter((p) => !p.company_id);
      } else {
        result = result.filter((p) => String(p.company_id) === companyFilter);
      }
    }

    // AI filter
    if (aiFilter !== "all") {
      result = result.filter((p) =>
        aiFilter === "yes" ? p.ai_enabled : !p.ai_enabled
      );
    }

    // Sort
    result.sort((a, b) => {
      if (sortBy === "name") {
        return (a.name || "").localeCompare(b.name || "");
      } else if (sortBy === "company") {
        const compA = companies.find((c) => c.id === a.company_id)?.name || "zzz";
        const compB = companies.find((c) => c.id === b.company_id)?.name || "zzz";
        return compA.localeCompare(compB);
      } else if (sortBy === "type") {
        return (a.type || "").localeCompare(b.type || "");
      } else if (sortBy === "date-newest") {
        return new Date(b.createdAt) - new Date(a.createdAt);
      } else if (sortBy === "date-oldest") {
        return new Date(a.createdAt) - new Date(b.createdAt);
      }
      return 0;
    });

    return result;
  }, [projects, companies, searchQuery, typeFilter, statusFilter, companyFilter, aiFilter, sortBy]);

  // Pagination
  const totalPages = Math.ceil(filteredProjects.length / itemsPerPage);
  const paginatedProjects = filteredProjects.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, typeFilter, statusFilter, companyFilter, aiFilter, sortBy]);

  const getCompanyName = (companyId) => {
    if (!companyId) return null;
    const company = companies.find((c) => c.id === companyId);
    return company?.name || "Unknown";
  };

  const handleOpenAddModal = () => {
    setIsEditing(false);
    setSelectedProject(null);
    setFormData({
      name: "",
      description: "",
      type: "chat",
      group_name: "",
      status: "1",
      ai_enabled: false,
      ai_chatbot_enabled: false,
      ai_phone_agent_enabled: false,
      company_id: "",
    });
    setLogoFile(null);
    setLogoPreview(null);
    setShowAddEditModal(true);
  };

  const handleOpenEditModal = (project) => {
    setIsEditing(true);
    setSelectedProject(project);
    setFormData({
      name: project.name || "",
      description: project.description || "",
      type: project.type || "chat",
      group_name: project.group_name || "",
      status: String(project.status ?? "1"),
      ai_enabled: project.ai_enabled || false,
      // Explicit chat/voice product flags come straight from clients.*_enabled
      // via fn_get_my_clients (Part 9 widened this). The legacy `type` field
      // in the projects list is now DERIVED from ai_phone_agent_enabled in
      // getAllProjects, so `type === "chat"` on a phone-enabled client would
      // return false and — pre-fix — silently toggled ai_chatbot_enabled OFF
      // on save. Default to false when the DB has no explicit value.
      ai_chatbot_enabled: project.ai_chatbot_enabled === true,
      ai_phone_agent_enabled: project.ai_phone_agent_enabled === true,
      company_id: project.company_id ? String(project.company_id) : "",
    });
    setLogoPreview(project.logoUrl || null);
    setLogoFile(null);
    setShowAddEditModal(true);
  };

  const handleFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleLogoChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        showError("Error", "Logo file size must be less than 5MB");
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => setLogoPreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);

    try {
      let projectId;

      if (isEditing && selectedProject) {
        const updateData = {
          name: formData.name,
          description: formData.description,
          type: formData.type,
          group_name: formData.group_name,
          status: parseInt(formData.status),
          ai_enabled: formData.ai_enabled,
          ai_chatbot_enabled: formData.ai_chatbot_enabled,
          ai_phone_agent_enabled: formData.ai_phone_agent_enabled,
        };

        await updateProject(selectedProject.id, updateData);
        projectId = selectedProject.id;

        // Handle organization assignment change. company_id is a UUID
        // (organization_id in Supabase); do NOT parseInt — that truncates the
        // UUID to its leading digit and results in "22P02 invalid input
        // syntax for type uuid" on the RPC.
        const oldCompanyId = selectedProject.company_id || null;
        const newCompanyId = formData.company_id || null;

        if (oldCompanyId !== newCompanyId) {
          if (oldCompanyId) {
            await removeProjectFromCompany(oldCompanyId, projectId);
          }
          if (newCompanyId) {
            await addProjectToCompany(newCompanyId, projectId);
          }
        }

        // Persist per-client feature-flag toggles via ClientFeatureToggles.
        // Component owns the diff logic; only-changed flags get written to
        // `feature_flags` (scope='client'). No-op if nothing changed.
        if (featureTogglesRef.current) {
          try { await featureTogglesRef.current.save(); }
          catch (fErr) { showError("Feature flags", fErr?.message || "Some feature toggles didn't save"); }
        }

        showSuccess("Success", "Client updated successfully");
      } else {
        const projectData = {
          name: formData.name,
          description: formData.description,
          type: formData.type,
          group_name: formData.group_name,
          ai_enabled: formData.ai_enabled,
          ai_chatbot_enabled: formData.ai_chatbot_enabled,
          ai_phone_agent_enabled: formData.ai_phone_agent_enabled,
          company_id: formData.company_id || null,
        };

        const response = await createProject(projectData);
        projectId = response?.data?.id || response?.id;
        showSuccess("Success", "Client created successfully");
      }

      // Upload logo if selected
      if (logoFile && projectId) {
        await uploadProjectLogo(projectId, logoFile);
      }

      setShowAddEditModal(false);
      fetchData();
      refreshViewProjects(); // refresh ViewContext so header picks up feature changes
      refreshClientFlags();  // reload feature_flags map so left-sidebar updates
      // for the currently-selected client without a full page reload
    } catch (err) {
      showError("Error", err?.message || "Failed to save client");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteProject = async (project) => {
    const confirmed = await showConfirm({
      title: "Delete Client",
      message: `Are you sure you want to delete "${project.name}"? This action cannot be undone.`,
      confirmText: "Delete",
      cancelText: "Cancel",
      confirmButtonClass: "bg-red-500 hover:bg-red-600",
    });

    if (!confirmed) return;

    try {
      await deleteProject(project.id);
      showSuccess("Success", "Project deleted successfully");
      fetchData();
    } catch (err) {
      showError("Error", err?.message || "Failed to delete project");
    }
  };

  const handleAssignCompany = (project) => {
    setSelectedProject(project);
    setShowAssignCompanyModal(true);
  };

  const handleCompanyAssignment = async (companyId) => {
    if (!selectedProject) return;

    try {
      const oldCompanyId = selectedProject.company_id;

      if (oldCompanyId) {
        await removeProjectFromCompany(oldCompanyId, selectedProject.id);
      }

      if (companyId) {
        await addProjectToCompany(companyId, selectedProject.id);
        showSuccess("Success", `Project assigned to ${getCompanyName(companyId)}`);
      } else {
        showSuccess("Success", "Client unassigned from organization");
      }

      setShowAssignCompanyModal(false);
      setSelectedProject(null);
      fetchData();
    } catch (err) {
      showError("Error", err?.message || "Failed to update organization assignment");
    }
  };

  // Stats
  const stats = useMemo(() => {
    const total = projects.length;
    const active = projects.filter((p) => p.status === 1).length;
    const withCompany = projects.filter((p) => p.company_id).length;
    const aiEnabled = projects.filter((p) => p.ai_enabled).length;
    return { total, active, withCompany, aiEnabled };
  }, [projects]);

  return (
    <HubShell>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="flex flex-col gap-3 mb-6 min-[450px]:flex-row min-[450px]:items-center min-[450px]:justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
              Client Management
            </h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Manage all clients and their organization assignments
            </p>
          </div>
          <div className="flex flex-col w-full gap-3 min-[450px]:flex-row min-[450px]:w-auto min-[450px]:items-center">
            <button
              onClick={handleOpenAddModal}
              className="w-full justify-center min-[450px]:w-auto px-4 py-2 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-lg font-semibold shadow-sm transition-all flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path d="M12 4v16m8-8H4" />
              </svg>
              Add Client
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg">
                <svg className="w-5 h-5 text-blue-600 dark:text-blue-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M2 6a2 2 0 012-2h12a2 2 0 012 2v2a2 2 0 100 4v2a2 2 0 01-2 2H4a2 2 0 01-2-2v-2a2 2 0 100-4V6z" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.total}</p>
                <p className="text-xs text-gray-500">Total Projects</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 dark:bg-green-900/30 rounded-lg">
                <svg className="w-5 h-5 text-green-600 dark:text-green-400" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.active}</p>
                <p className="text-xs text-gray-500">Active</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 dark:bg-emerald-900/30 rounded-lg">
                <svg className="w-5 h-5 text-emerald-600 dark:text-emerald-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.withCompany}</p>
                <p className="text-xs text-gray-500">With Company</p>
              </div>
            </div>
          </div>
          <div className="bg-white dark:bg-gray-900 rounded-xl p-4 shadow border dark:border-gray-800">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M13 7H7v6h6V7z" />
                  <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                </svg>
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{stats.aiEnabled}</p>
                <p className="text-xs text-gray-500">AI Enabled</p>
              </div>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4">
            {/* Search */}
            <div className="lg:col-span-2">
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Search</label>
              <div className="relative">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search projects..."
                  className="w-full border dark:border-gray-700 rounded-lg px-10 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
                />
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                  <circle cx="11" cy="11" r="7" />
                  <path d="M21 21l-4.3-4.3" />
                </svg>
              </div>
            </div>

            {/* Type */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Type</label>
              <select
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">All Types</option>
                <option value="chat">Chat</option>
                <option value="voice">Voice</option>
              </select>
            </div>

            {/* Status */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Status</label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">All Status</option>
                <option value="1">Active</option>
                <option value="0">Inactive</option>
              </select>
            </div>

            {/* Company */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">Company</label>
              <select
                value={companyFilter}
                onChange={(e) => setCompanyFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">All Companies</option>
                <option value="unassigned">⚠️ Unassigned</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>
            </div>

            {/* AI */}
            <div>
              <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-2 block">AI Status</label>
              <select
                value={aiFilter}
                onChange={(e) => setAiFilter(e.target.value)}
                className="w-full border dark:border-gray-700 rounded-lg px-3 py-2.5 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-violet-500"
              >
                <option value="all">All</option>
                <option value="yes">⚡ AI Enabled</option>
                <option value="no">AI Disabled</option>
              </select>
            </div>
          </div>

          {/* Sort */}
          <div className="mt-4 flex items-center gap-3">
            <label className="text-xs font-medium text-gray-700 dark:text-gray-300">Sort:</label>
            <div className="flex gap-2 flex-wrap">
              {[
                { value: "name", label: "Name" },
                { value: "company", label: "Organization" },
                { value: "type", label: "Type" },
                { value: "date-newest", label: "Newest" },
                { value: "date-oldest", label: "Oldest" },
              ].map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${
                    sortBy === opt.value
                      ? "bg-violet-500 text-white"
                      : "bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700"
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          </div>
        ) : filteredProjects.length === 0 ? (
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 p-12 text-center">
            <svg className="w-16 h-16 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" strokeWidth="1.5" viewBox="0 0 24 24">
              <path d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
            </svg>
            <p className="text-gray-500 dark:text-gray-400 font-medium">No projects found</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Try adjusting your filters or create a new project</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
              {paginatedProjects.map((project) => (
                <div
                  key={project.id}
                  className="bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 overflow-hidden hover:shadow-lg transition-shadow"
                >
                  <div className="p-5">
                    <div className="flex items-start gap-3 mb-3">
                      <img 
                        src={project.logoUrl || "/Rigsby.png"} 
                        alt={project.name} 
                        className="w-12 h-12 rounded-lg object-cover border dark:border-gray-700" 
                        onError={(e) => {
                          e.target.src = "/Rigsby.png";
                        }}
                      />
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 dark:text-gray-100 truncate">{project.name}</h3>
                        <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            project.type === "voice"
                              ? "bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300"
                              : "bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300"
                          }`}>
                            {project.type || "chat"}
                          </span>
                          <span className={`px-2 py-0.5 text-xs font-medium rounded ${
                            project.status === 1
                              ? "bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300"
                          }`}>
                            {project.status === 1 ? "Active" : "Inactive"}
                          </span>
                          {project.ai_enabled && (
                            <span className="px-2 py-0.5 text-xs font-medium rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
                              ⚡ AI
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {project.description && (
                      <p className="text-sm text-gray-500 dark:text-gray-400 mb-3 line-clamp-2">{project.description}</p>
                    )}

                    {/* Company Badge */}
                    <div className="mb-3">
                      {project.company_id ? (
                        <button
                          onClick={() => handleAssignCompany(project)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-lg text-emerald-700 dark:text-emerald-300 text-xs font-medium hover:bg-emerald-100 dark:hover:bg-emerald-900/40 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          {getCompanyName(project.company_id)}
                        </button>
                      ) : (
                        <button
                          onClick={() => handleAssignCompany(project)}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-800 rounded-lg text-orange-700 dark:text-orange-300 text-xs font-medium hover:bg-orange-100 dark:hover:bg-orange-900/40 transition-colors"
                        >
                          <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                          </svg>
                          No Organization Assigned
                        </button>
                      )}
                    </div>

                    {/* Actions */}
                    <div className="space-y-2 pt-3 border-t dark:border-gray-800">
                      <div className="flex gap-2">
                        <button
                          onClick={() => handleOpenEditModal(project)}
                          className="flex-1 px-3 py-1.5 text-sm border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 text-gray-700 dark:text-gray-300 transition-colors"
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => handleDeleteProject(project)}
                          className="flex-1 px-3 py-1.5 text-sm border border-red-200 dark:border-red-800 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 text-red-600 dark:text-red-400 transition-colors"
                        >
                          Delete
                        </button>
                      </div>
                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setShowExportModal(true);
                        }}
                        className="w-full px-3 py-1.5 text-sm bg-gradient-to-r from-[#32B1FF] to-blue-600 text-white rounded-lg hover:from-[#259ae6] hover:to-blue-700 transition-all flex items-center justify-center gap-1.5 shadow-sm"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                        </svg>
                        Export Evaluations
                      </button>
                      <button
                        onClick={() => {
                          setSelectedProject(project);
                          setShowSyncModal(true);
                        }}
                        className="w-full px-3 py-1.5 text-sm bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg hover:from-purple-600 hover:to-purple-700 transition-all flex items-center justify-center gap-1.5 shadow-sm"
                        title="Configure automatic transcript syncing"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                        </svg>
                        Auto-Sync
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex flex-col gap-3 min-[412px]:flex-row min-[412px]:items-center min-[412px]:justify-between bg-white dark:bg-gray-900 rounded-xl shadow border dark:border-gray-800 px-6 py-4">
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-600 dark:text-gray-400">Show</label>
                  <select
                    value={itemsPerPage}
                    onChange={(e) => {
                      setItemsPerPage(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border dark:border-gray-700 rounded px-2 py-1 text-sm bg-white dark:bg-gray-800"
                  >
                    <option value="12">12</option>
                    <option value="24">24</option>
                    <option value="48">48</option>
                  </select>
                  <span className="text-sm text-gray-600 dark:text-gray-400">
                    Showing {(currentPage - 1) * itemsPerPage + 1}-{Math.min(currentPage * itemsPerPage, filteredProjects.length)} of {filteredProjects.length}
                  </span>
                </div>
                <div className="flex flex-wrap gap-1">
                  <button
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Previous
                  </button>
                  <button
                    onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="px-3 py-1.5 border dark:border-gray-700 rounded-lg text-sm hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Add/Edit Client Modal */}
      {showAddEditModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b dark:border-gray-800">
              <h2 className="text-xl font-bold text-gray-900 dark:text-gray-100">
                {isEditing ? "Edit Client" : "Add New Client"}
              </h2>
            </div>

            <form onSubmit={handleSubmit} className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleFormChange}
                    required
                    className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800"
                  />
                </div>

                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleFormChange}
                    rows="2"
                    className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Status</label>
                  <select name="status" value={formData.status} onChange={handleFormChange} className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800">
                    <option value="1">Active</option>
                    <option value="0">Inactive</option>
                  </select>
                </div>

                {/* Voiceflow credentials moved to VoiceflowIntegrationSection on
                    the Client Detail page — they use real Supabase columns
                    (voiceflow_project_id_chats / voiceflow_api_key / etc.) and
                    have proper RLS + credential-masking handling there. */}

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Assign to Organization</label>
                  <select name="company_id" value={formData.company_id} onChange={handleFormChange} className="w-full border dark:border-gray-700 rounded-lg px-3 py-2 bg-white dark:bg-gray-800">
                    <option value="">No Organization</option>
                    {companies.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                </div>

                {/* Supabase Client picker removed 2026-07-27 — legacy field.
                    Post-migration `clients.id` IS the Supabase client id, so
                    no explicit linking step is needed. */}

                {/* Logo */}
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Project Logo</label>
                  <div className="flex items-center gap-4">
                    {logoPreview ? (
                      <div className="relative">
                        <img src={logoPreview} alt="" className="w-16 h-16 rounded-lg object-cover border" />
                        <button type="button" onClick={() => { setLogoFile(null); setLogoPreview(null); }} className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full text-xs">×</button>
                      </div>
                    ) : (
                      <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 dark:border-gray-700 flex items-center justify-center">
                        <svg className="w-6 h-6 text-gray-400" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                    <label className="cursor-pointer px-4 py-2 bg-gray-100 dark:bg-gray-800 hover:bg-gray-200 dark:hover:bg-gray-700 rounded-lg text-sm">
                      Choose File
                      <input type="file" accept="image/*" onChange={handleLogoChange} className="hidden" />
                    </label>
                  </div>
                </div>

                {/* AI Capabilities */}
                <div className="md:col-span-2">
                  <div className="flex items-center justify-between p-4 rounded-lg border dark:border-gray-700 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="currentColor" viewBox="0 0 20 20">
                          <path d="M13 7H7v6h6V7z" />
                          <path fillRule="evenodd" d="M7 2a1 1 0 012 0v1h2V2a1 1 0 112 0v1h2a2 2 0 012 2v2h1a1 1 0 110 2h-1v2h1a1 1 0 110 2h-1v2a2 2 0 01-2 2h-2v1a1 1 0 11-2 0v-1H9v1a1 1 0 11-2 0v-1H5a2 2 0 01-2-2v-2H2a1 1 0 110-2h1V9H2a1 1 0 010-2h1V5a2 2 0 012-2h2V2zM5 5h10v10H5V5z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">AI Tagging &amp; Summaries</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Enable automated sentiment tagging and conversation summaries</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" name="ai_enabled" checked={formData.ai_enabled} onChange={handleFormChange} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-amber-500"></div>
                    </label>
                  </div>
                </div>

                {/* AI Chat product entitlement — writes to clients.ai_chatbot_enabled */}
                <div>
                  <div className="flex items-center justify-between p-4 rounded-lg border dark:border-gray-700 bg-gradient-to-r from-teal-50 to-cyan-50 dark:from-teal-900/20 dark:to-cyan-900/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-teal-500 to-cyan-500 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">AI Chat</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Enable the chat agent for this property</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" name="ai_chatbot_enabled" checked={formData.ai_chatbot_enabled} onChange={handleFormChange} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                    </label>
                  </div>
                </div>

                {/* AI Voice product entitlement — writes to clients.ai_phone_agent_enabled */}
                <div>
                  <div className="flex items-center justify-between p-4 rounded-lg border dark:border-gray-700 bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-blue-500 to-indigo-500 flex items-center justify-center">
                        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                        </svg>
                      </div>
                      <div>
                        <p className="font-semibold text-gray-900 dark:text-gray-100 text-sm">AI Voice</p>
                        <p className="text-xs text-gray-600 dark:text-gray-400">Enable the phone agent for this property</p>
                      </div>
                    </div>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input type="checkbox" name="ai_phone_agent_enabled" checked={formData.ai_phone_agent_enabled} onChange={handleFormChange} className="sr-only peer" />
                      <div className="w-11 h-6 bg-gray-200 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-500"></div>
                    </label>
                  </div>
                </div>

                {/* Per-client dashboard/feature toggles. Reads + writes to
                    `feature_flags` (scope='client', scope_id=<client.id>).
                    Only shown in edit mode — creation flow doesn't have a
                    client_id yet; open a fresh client, then edit to toggle
                    features. */}
                {isEditing && selectedProject?.id && (
                  <div className="md:col-span-2">
                    <ClientFeatureToggles
                      ref={featureTogglesRef}
                      clientId={selectedProject.id}
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-4">
                <button type="button" onClick={() => setShowAddEditModal(false)} className="flex-1 px-4 py-2.5 border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 px-4 py-2.5 bg-gradient-to-r from-violet-500 to-purple-500 hover:from-violet-600 hover:to-purple-600 text-white rounded-lg disabled:opacity-50 font-medium">
                  {saving ? "Saving..." : isEditing ? "Update Client" : "Create Client"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Assign Company Modal */}
      {showAssignCompanyModal && selectedProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full">
            <div className="p-6 border-b dark:border-gray-800">
              <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
                Assign Company
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Select an organization for "{selectedProject.name}"
              </p>
            </div>

            <div className="p-6 space-y-2 max-h-80 overflow-y-auto">
              <button
                onClick={() => handleCompanyAssignment(null)}
                className={`w-full p-3 text-left rounded-lg border transition-colors ${
                  !selectedProject.company_id
                    ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                    : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                }`}
              >
                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">No Company (Unassigned)</span>
              </button>
              {companies.map((company) => (
                <button
                  key={company.id}
                  onClick={() => handleCompanyAssignment(company.id)}
                  className={`w-full p-3 text-left rounded-lg border transition-colors flex items-center gap-3 ${
                    selectedProject.company_id === company.id
                      ? "border-violet-500 bg-violet-50 dark:bg-violet-900/20"
                      : "border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800"
                  }`}
                >
                  {company.logoUrl ? (
                    <img src={company.logoUrl} alt="" className="w-8 h-8 rounded object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded bg-emerald-500 flex items-center justify-center text-white text-xs font-bold">
                      {company.name?.[0]?.toUpperCase()}
                    </div>
                  )}
                  <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{company.name}</span>
                </button>
              ))}
            </div>

            <div className="p-4 border-t dark:border-gray-800">
              <button
                onClick={() => { setShowAssignCompanyModal(false); setSelectedProject(null); }}
                className="w-full px-4 py-2 border dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Transcript Evaluation Export Modal */}
      <TranscriptEvaluationExportModal
        open={showExportModal}
        onClose={() => {
          setShowExportModal(false);
          setSelectedProject(null);
        }}
        project={selectedProject}
      />

      {/* Transcript Auto-Sync Modal */}
      {showSyncModal && (
        <TranscriptSyncModal
          project={selectedProject}
          onClose={() => {
            setShowSyncModal(false);
            setSelectedProject(null);
          }}
          onUpdate={() => {
            fetchData(); // Refresh projects list to show updated sync status
          }}
        />
      )}
    </HubShell>
  );
};

export default ProjectManagementPage;



